import { computed, Injectable, signal } from '@angular/core';
import { DraftDocument, DraftEntity, DraftStatus } from '@panaya/ai-chat/shared';

/**
 * The draft as seemore last sent it, and which of its entities the user has checked.
 * Written by the draft interceptor and by the displays' checkboxes. It holds the document as it arrived, the agent's
 * their data included, and never converts one: the displays do that when they render. Every update replaces the
 * document whole, never merges.
 */
@Injectable({ providedIn: 'root' })
export class DraftStore {
    private readonly _draft = signal<DraftDocument | null>(null);

    private readonly _selection = signal<ReadonlySet<number>>(new Set());

    public readonly draft = this._draft.asReadonly();

    public readonly version = computed(() => this._draft()?.version ?? 0);

    public readonly status = computed((): DraftStatus | null => this._draft()?.status ?? null);

    public readonly message = computed(() => this._draft()?.message ?? null);

    public readonly entities = computed((): DraftEntity[] => this._draft()?.entities ?? []);

    public readonly readyCount = computed(() => this.entities().filter((entity) => entity.ready).length);

    public readonly isReady = computed(() => this.status() === 'ready');

    /** The checked ids, in the document's order; what `selected_ids` carries on the next prompt. */
    public readonly selectedIds = computed(() =>
        this.entities()
            .map((entity) => entity.id)
            .filter((id) => this._selection().has(id))
    );

    public readonly selectedIdSet = this._selection.asReadonly();

    public readonly selectedCount = computed(() => this.selectedIds().length);

    /**
     * Applies a draft update (`canvas_update` with `content: 'draft'`). Returns `false` when it is ignored: the same draft at a version lower than or equal to
     * the one held. A different `draft_id` is a new draft and replaces the held one whatever its version.
     */
    public apply(incoming: DraftDocument): boolean {
        const current = this._draft();

        const sameDraft = current !== null && current.draft_id === incoming.draft_id;

        if (sameDraft && incoming.version <= current.version) {
            return false;
        }

        this._selection.set(nextSelection(sameDraft ? current.entities : [], this._selection(), incoming.entities));

        this._draft.set(incoming);

        return true;
    }

    /** A checkbox click. An entity that is not ready cannot be checked. Nothing is sent: selection travels with the next prompt. */
    public setSelected(id: number, selected: boolean): void {
        const entity = this.entities().find((candidate) => candidate.id === id);

        if (!entity?.ready) {
            return;
        }

        const next = new Set(this._selection());

        if (selected) {
            next.add(id);
        } else {
            next.delete(id);
        }

        this._selection.set(next);
    }

    public reset(): void {
        this._draft.set(null);

        this._selection.set(new Set());
    }
}

/**
 * The selection rules: an entity that was ready before keeps its checkbox; an entity that is new, or has
 * just turned ready, starts checked; an id that is gone is dropped. With no previous entities, every ready one is checked.
 */
function nextSelection(previous: DraftEntity[], selection: ReadonlySet<number>, incoming: DraftEntity[]): ReadonlySet<number> {
    const wasReady = new Set(previous.filter((entity) => entity.ready).map((entity) => entity.id));

    const keepsChoice = (id: number): boolean => wasReady.has(id);

    return new Set(
        incoming.filter((entity) => entity.ready && (!keepsChoice(entity.id) || selection.has(entity.id))).map((entity) => entity.id)
    );
}
